import { InternalId, IPagedListResponse, RuntimeDate } from '@edgebox/data-definition-kit';
import { Alert, Button, Form, Modal, Table } from '@edgebox/react-components';
import {
  ContentPriority,
  IRemoteEntityReference,
  ISSUE_NOTE_MAX_LENGTH,
  ISyncCoreContentItem,
  ISyncCoreTaxonomyTerm,
  IssueResolutionResponsibility,
  IssueStatus,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import {
  ClientContentOptimizationTypeEntity,
  ClientIssueEntity,
  ClientSiteEntity,
  IIssueListFilters,
  IPatchIssueStatusRequest,
} from '@edgebox/sync-core-rest-client';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Link } from 'react-router-dom';
import { faArrowUpRightFromSquare, faChevronDown, faCircleCheck, faLayerGroup, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { BrandPresencePagination } from './Components/BrandPresencePagination';
import { BrandPresenceEmptyState } from './Components/BrandPresenceEmptyState';
import { BrandPresenceSiteFrameContext } from './BrandPresenceSiteFrame';
import { IndeterminateProgress, SkeletonBlock } from './Components/BrandPresenceSkeleton';
import { BRAND_PRESENCE_LIST_PAGE_SIZES, pageSizeToToken, parsePageSizeToken } from './Components/brandPresenceLists';
import {
  IIssuesFilterNamesLoading,
  IssuesAddFilterDropdown,
  IIssuesFilterValues,
  NAME_NOT_AVAILABLE_COPY,
} from './Components/IssuesAddFilterDropdown';
import { IIssueStatusChoice, IssueStatusControl, IssueStatusPill, issueStatusLabel } from './Components/IssueStatusControl';
import { IOptimizationView, IssueOptimizationFollower, IssueOptimizeAction } from './Components/IssueOptimizeAction';
import {
  IStartedOptimization,
  loadActiveOptimizationTypes,
  optimizationTypeKeysByIssueTypeKey,
  optimizationTypesByKeys,
} from './Components/checkOptimization.client';
import { HealthCheckNameReference } from './Onboarding/ContentInventory/Components/HealthCheckNameReference';
import { getCachedTaxonomyTermsByFacet } from './Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import {
  BP_CARD_CLASSES,
  BP_PORTAL_CLASS_NAME,
  BP_TABLE_HEAD_CELL_CLASSES,
  BP_TOOLBAR_BUTTON_CLASSES,
  BP_TOOLBAR_BUTTON_OPEN_CLASSES,
  BP_TOOLBAR_BUTTON_SMALL_CLASSES,
} from './Components/brandPresenceClasses';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { AppParamsContext, IAppParams } from '../../AppParams';
import { ISSUE_WRITE_OWN_SCOPE } from '../../../features';
import { sendToParent } from '../../../frame-messages';
import { getSessionScopes } from '../../../services/SyncCoreApiComponent';
import {
  searchOfBrandPresenceScreenAddress,
  useBrandPresenceScreenAddress,
  useReportBrandPresenceScreenQuery,
} from './brandPresenceScreenQuery';
import { EmbeddedModal } from '../../EmbeddedModal';
import { isPlatformGovernanceSite, onlyRegularSites } from '../../siteKind.utils';
import { clientErrorStatus } from './Components/clientErrorTerminal';
import {
  BRAND_PRESENCE_CONTENT_INVENTORY_PATH,
  BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN,
  BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS,
  BRAND_PRESENCE_ISSUES_QUERY_PARAMS,
  BRAND_PRESENCE_ISSUES_VIEW_BY_PAGE_TOKEN,
  brandPresenceContentItemHealthPath,
  brandPresenceContentItemPath,
} from './brandPresencePaths';
import {
  daysSince,
  evidenceValueText,
  ISSUE_PRIORITY_PILL_CLASSES,
  issueCountLabel,
  urlDisplayPath,
  withoutSiteName,
} from './governanceIssues.utils';

/** Every body cell of the list. */
const CELL_CLASSES = 'px-4 py-3.5 text-sm';

/** A row's page as a link, which reads as one. */
const PAGE_LINK_CLASSES =
  'font-medium text-primary underline decoration-primary/30 underline-offset-2 transition-colors hover:text-primary hover:decoration-primary';

/** The page column's width, the same while it loads as once its rows arrive: a few words, at most a short line. */
const PAGE_CELL_WIDTH_CLASSES = 'min-w-[12rem] max-w-[280px]';

/** Where the page column sits among the list's columns: after the row's controls, the status, the priority and the type. */
const PAGE_COLUMN_INDEX = 4;

/** Keeps a cue on the line of the word before it. */
const NO_BREAK_SPACE = ' ';

/** Loading bars of uneven widths, so the skeleton reads as rows of text. */
const SKELETON_CELL_WIDTHS = ['w-6', 'w-16', 'w-16', 'w-3/4', 'w-2/3', 'w-24', 'w-28', 'w-12'];
/** How many sites the Site filter's options are read at a time. */
const SITE_OPTIONS_PAGE_SIZE = 100;
/** How many of those pages it reads, so one very large project cannot hold the filter open. */
const SITE_OPTIONS_MAX_PAGES = 20;
const SKELETON_ROW_COUNT = 5;
const EM_DASH = '—';
/** The order the list is asked for: priority first, then most recently seen, computed by the server. */
const ISSUE_ORDER = '-priority';
/** What the row says when the server refuses a status change the session has no scope for. */
const PERMISSION_DENIED_COPY = "You do not have permission to change an issue's status.";
/** A status-change failure that is neither a refusal nor an issue closed elsewhere. */
const STATUS_CHANGE_FAILED_COPY = "The issue's status could not be changed. Please try again.";
/** A status change sent for an issue someone else already closed. */
const ALREADY_CLOSED_COPY = 'This issue has already been closed.';
/**
 * What a row's controls and messages call an issue whose type could not be read: its key is an
 * identifier and is never shown.
 */
const UNNAMED_ISSUE_LABEL = 'this issue';
/** What a row says when Sync Core refuses its change: closed elsewhere, not permitted, or failed. */
function refusalCopy(status: number | undefined): string {
  return status === HTTP_CONFLICT ? ALREADY_CLOSED_COPY : status === HTTP_FORBIDDEN ? PERMISSION_DENIED_COPY : STATUS_CHANGE_FAILED_COPY;
}

/** What a row's controls and messages call an issue: its type's name, or the words for an unnamed one. */
function issueLabelOf(tagName: string | undefined): string {
  return tagName || UNNAMED_ISSUE_LABEL;
}

/** What a cell says for an issue type, or a page, the row references but could not read. */
const ISSUE_TYPE_NOT_AVAILABLE_COPY = 'Issue type not available';
const PAGE_NOT_AVAILABLE_COPY = 'Page not available';
/** What a cell says for an issue that belongs to no page. */
const NO_PAGE_COPY = 'No page';
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const VERDICT_NOTE_ID = 'issue-verdict-note';
const BULK_NOTE_ID = 'issues-bulk-note';
/** The two closing statuses are final, so the status says why it offers no change. */
const TERMINAL_TITLE = 'A closed issue cannot be reopened.';
/** The compact date the row speaks: the year would say nothing a triage decision needs. */
const ROW_DATE_FORMAT = 'MMM DD';
/** The full date a compact one reveals on hover. */
const ROW_DATE_FULL_FORMAT = 'MMM DD, YYYY';

/**
 * What the live region says while a row's status change is waiting on the server. The row's control
 * stays reachable while it waits, so the wait itself is spoken rather than left to be guessed at.
 */
function savingStatusCopy(tagLabel: string): string {
  return `Saving the status of ${tagLabel}.`;
}

/**
 * What the live region says when a choice reaches a row whose own write is still running. The
 * row's control stays reachable while it waits, so the refusal is the answer the person gets, and
 * these words differ from the wait's, which is what makes a polite region speak them.
 */
function refusedWhileSavingCopy(tagLabel: string): string {
  return `Still saving the status of ${tagLabel}. That change was not made.`;
}

// The query parameters and the status tokens this screen reads its filters from are URL vocabulary,
// never a word a reader sees, and they are declared with the area's paths: a screen that links here
// and this screen reading the link apart cannot spell one of them differently.

/** The status the screen opens on when the address names none. */
const DEFAULT_STATUS = IssueStatus.Open;

const STATUS_VALUES = Object.values(IssueStatus);

/**
 * The decisions a row or a selection can take, each with the words its controls, its explanation
 * line and its confirmations read. The explanation says what the decision means, so the difference
 * between ignoring an issue and rejecting it is on the control itself.
 */
const FIXED_DECISION = {
  id: 'fixed',
  status: IssueStatus.Fixed,
  action: 'Mark as fixed',
  explanation: 'The fix is already live on the page.',
  doneOne: 'Issue marked as fixed.',
} as const;

const IGNORED_DECISION = {
  id: 'ignored',
  status: IssueStatus.Ignored,
  action: 'Ignore',
  explanation: 'A real issue you choose not to fix.',
  doneOne: 'Issue ignored.',
} as const;

const FALSE_POSITIVE_DECISION = {
  id: 'false-positive',
  status: IssueStatus.FalsePositive,
  action: 'Not an issue',
  explanation: 'The check got it wrong.',
  doneOne: 'Issue marked as not an issue.',
} as const;

const ISSUE_DECISIONS = [FIXED_DECISION, IGNORED_DECISION, FALSE_POSITIVE_DECISION] as const;

type IIssueDecision = (typeof ISSUE_DECISIONS)[number];

/** What a fixed issue offers in place of Mark as fixed: back onto the open list. */
const REOPEN_DECISION = {
  id: 'reopen',
  status: IssueStatus.Open,
  action: 'Reopen',
  explanation: 'Put this issue back on the open list.',
  doneOne: 'Issue reopened.',
} as const;

/**
 * The two decisions that close an issue for good, which confirm in a dialog: what the dialog asks,
 * and what it says the decision does. The action and the words said once it is done are the
 * decision's own.
 */
const ISSUE_VERDICTS = [
  {
    decision: IGNORED_DECISION,
    title: 'Ignore this issue?',
    explanation: 'An ignored issue stays closed: a later crawl does not reopen it.',
  },
  {
    decision: FALSE_POSITIVE_DECISION,
    title: 'Mark this as not an issue?',
    explanation: 'An issue marked as not an issue stays closed: a later crawl does not reopen it.',
  },
] as const;

type IIssueVerdict = (typeof ISSUE_VERDICTS)[number];

/** The verdict a decision confirms through, where it closes the issue for good. */
function verdictOf(decision: IIssueDecision): IIssueVerdict | undefined {
  return ISSUE_VERDICTS.find((verdict) => verdict.decision.status === decision.status);
}

/** What the bulk confirmation of a decision asks for a selection of the given size. */
function bulkTitle(decision: IIssueDecision, count: number): string {
  if (decision.status === IssueStatus.Ignored) {
    return count === 1 ? 'Ignore this issue?' : `Ignore ${count} issues?`;
  }
  if (decision.status === IssueStatus.FalsePositive) {
    return count === 1 ? 'Mark this as not an issue?' : `Mark ${count} issues as not an issue?`;
  }
  return count === 1 ? 'Mark this issue as fixed?' : `Mark ${count} issues as fixed?`;
}

function bulkDoneCopy(decision: IIssueDecision, count: number): string {
  if (count === 1) {
    return decision.doneOne;
  }
  if (decision.status === IssueStatus.Ignored) {
    return `${count} issues ignored.`;
  }
  if (decision.status === IssueStatus.FalsePositive) {
    return `${count} issues marked as not an issue.`;
  }
  return `${count} issues marked as fixed.`;
}

function bulkFailedCopy({ failed, total }: { failed: number; total: number }): string {
  return `${failed} of ${total} issues could not be changed. Try again.`;
}

/** What a bulk decision says of the picked issues someone else had closed before its writes arrived. */
function bulkAlreadyClosedCopy(count: number): string {
  return count === 1 ? '1 issue had already been closed.' : `${count} issues had already been closed.`;
}

/** How a status change ended: written, refused with the row as it was, or refused because it was already closed. */
type IStatusChangeOutcome = 'changed' | 'failed' | 'already-closed';

/** The row a verdict dialog is open for. */
interface IVerdictDraft {
  issue: ClientIssueEntity;
  verdict: IIssueVerdict;
  /** The name of the issue's type, where it could be read. */
  tagName: string | undefined;
}

/** The decision the bulk confirmation is open for, and the rows it was opened with. */
interface IBulkDraft {
  decision: IIssueDecision;
  issues: ClientIssueEntity[];
}

/**
 * The two statuses Sync Core keeps for good: an issue a person ignored, or said is not an issue,
 * takes no further change, so its row offers none.
 */
function isTerminalStatus(status: IssueStatus): boolean {
  return status === IssueStatus.Ignored || status === IssueStatus.FalsePositive;
}

/** The paging figures the table and the pager read, taken straight from the client's list response. */
type IIssuePage = Pick<IPagedListResponse<ClientIssueEntity>, 'page' | 'numberOfPages' | 'totalNumberOfItems'>;

function responsibilityLabel(responsibility: IssueResolutionResponsibility): string {
  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('IssueResolutionResponsibility', responsibility);
}

/** The status the address token names, the default when it names none, and nothing when it says any. */
function statusFromToken(token: string | null): IssueStatus | undefined {
  if (token === null) {
    return DEFAULT_STATUS;
  }
  if (token === BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN) {
    return undefined;
  }
  const matched = STATUS_VALUES.find((status) => BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS[status] === token);
  return matched ?? DEFAULT_STATUS;
}

/** The responsibility the address token names, or nothing when it names none the enum knows. */
function responsibilityFromToken(token: string | null): IssueResolutionResponsibility | undefined {
  if (!token) {
    return undefined;
  }
  return Object.values(IssueResolutionResponsibility).find((value) => value === token);
}

/**
 * The statuses a row's control offers: an open issue its three decisions, a fixed one Reopen in
 * place of Mark as fixed. The two closing statuses offer nothing, because Sync Core keeps them.
 */
function statusChoicesFor(status: IssueStatus): IIssueStatusChoice[] {
  if (isTerminalStatus(status)) {
    return [];
  }
  return ISSUE_DECISIONS.map((decision) =>
    decision.status === IssueStatus.Fixed && status === IssueStatus.Fixed
      ? { id: REOPEN_DECISION.id, label: REOPEN_DECISION.action, explanation: REOPEN_DECISION.explanation }
      : { id: decision.id, label: decision.action, explanation: decision.explanation }
  );
}

/**
 * The tags the issues reference, resolved once per tag and kept under the key the reference carries.
 *
 * The name a row and the Issue type filter show lives on the tag, not on the issue, so it is looked
 * up beside the issues rather than folded into them. A reference that does not resolve leaves no
 * entry: its key is an identifier, so the row and the filter name that type by nothing.
 */
async function resolveTagsByKey(issues: ClientIssueEntity[]): Promise<Record<string, ISyncCoreTaxonomyTerm>> {
  const references = new Map<string, ClientIssueEntity['issueType']>();
  for (const issue of issues) {
    references.set(issue.issueType.getStableKey(), issue.issueType);
  }

  const byKey: Record<string, ISyncCoreTaxonomyTerm> = {};
  await Promise.all(
    Array.from(references, async ([key, reference]) => {
      try {
        const tag = await reference.get();
        if (tag) {
          byKey[key] = tag;
        }
      } catch {
        // The row says its issue type is not available.
      }
    })
  );
  return byKey;
}

/**
 * The pages the issues reference, resolved once per page and kept under the key the reference
 * carries. The key is the identifier the service gives a page, not its URL, so this is where a row
 * and the Page filter read the title and the URL from; a page that does not resolve is not named.
 */
async function resolvePagesByKey(issues: ClientIssueEntity[]): Promise<Record<string, ISyncCoreContentItem>> {
  const references = new Map<string, NonNullable<ClientIssueEntity['contentItem']>>();
  for (const issue of issues) {
    const reference = issue.contentItem;
    if (reference) {
      references.set(reference.getStableKey(), reference);
    }
  }

  const byKey: Record<string, ISyncCoreContentItem> = {};
  await Promise.all(
    Array.from(references, async ([key, reference]) => {
      try {
        const page = await reference.get();
        if (page) {
          byKey[key] = page;
        }
      } catch {
        // The row says its page is not available.
      }
    })
  );
  return byKey;
}

/**
 * The names of the sites the issues belong to, read once per site and kept under the site's id. A
 * crawled page title carries its site's name, which a row leaves off; a site that cannot be read
 * leaves no entry, and its pages keep their titles whole.
 */
async function resolveSiteNamesById(issues: ClientIssueEntity[]): Promise<Record<string, string>> {
  const references = new Map<string, NonNullable<ClientIssueEntity['site']>>();
  for (const issue of issues) {
    const reference = issue.site;
    if (reference) {
      references.set(String(reference.getId()), reference);
    }
  }

  const byId: Record<string, string> = {};
  await Promise.all(
    Array.from(references, async ([id, reference]) => {
      try {
        const site = await reference.get();
        if (site?.name) {
          byId[id] = site.name;
        }
      } catch {
        // The site's pages keep their titles whole.
      }
    })
  );
  return byId;
}

/** The entries of a record whose key passes the test; the record itself where every one does. */
function keepEntries<T>({ record, keep }: { record: Record<string, T>; keep: (key: string) => boolean }): Record<string, T> {
  const kept = Object.entries(record).filter(([key]) => keep(key));
  return kept.length === Object.keys(record).length ? record : Object.fromEntries(kept);
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  const next = { ...record };
  delete next[key];
  return next;
}

function formatRowDate(value?: RuntimeDate): string | undefined {
  return value ? value.format(ROW_DATE_FORMAT) : undefined;
}

/**
 * What a cell shows when it has nothing to name: the dash a reader sees, and the words a reader who
 * cannot see it hears. The dash carries no meaning of its own and an `aria-label` on a span that
 * holds no role names nothing, so the name is text of its own, kept out of sight.
 */
function MissingValue({ children }: { children: string }) {
  return (
    <>
      <span aria-hidden="true">{EM_DASH}</span>
      <span className="sr-only">{children}</span>
    </>
  );
}

/** A small pill naming an issue's priority, coloured by its level. */
function IssuePriorityPill({ issue }: { issue: ClientIssueEntity }) {
  if (!issue.priority) {
    return <MissingValue>No priority</MissingValue>;
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${ISSUE_PRIORITY_PILL_CLASSES[issue.priority]}`}
    >
      {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyTermPriority', issue.priority)}
    </span>
  );
}

/**
 * The page's own priority, with the page and named as the page's: a row's Priority column holds the
 * issue's priority alone. It is words, the way the content inventory shows a page's priority.
 */
function PagePriority({ priority }: { priority: ContentPriority | undefined }) {
  if (!priority) {
    return null;
  }
  return (
    <span className="text-xs text-slate-500">
      Page priority:{' '}
      <span className="font-medium text-slate-700">
        {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('ContentPriority', priority)}
      </span>
    </span>
  );
}

/**
 * The parts of a page's identity on its site that the site's Optimize screen is opened by, under the
 * names the page's remote entity reference gives them, so the site reads them in its own terms.
 */
const SITE_OPTIMIZE_IDENTITY: readonly (keyof IRemoteEntityReference)[] = [
  'entityTypeNamespaceMachineName',
  'entityTypeMachineName',
  'remoteUuid',
  'remoteUniqueId',
  'language',
];

/**
 * The address of a page's Optimize screen on the site hosting the frame: the address the site named,
 * with the page's identity on that site. A page the site did not name, or an address that is not one,
 * has none.
 */
function siteOptimizeHrefOf({ address, entity }: { address: string; entity: IRemoteEntityReference | undefined }): string | undefined {
  if (!entity || !(entity.remoteUuid || entity.remoteUniqueId)) {
    return undefined;
  }
  try {
    const href = new URL(address);
    for (const name of SITE_OPTIMIZE_IDENTITY) {
      const value = entity[name];
      if (typeof value === 'string' && value) {
        href.searchParams.set(name, value);
      }
    }
    return href.toString();
  } catch {
    return undefined;
  }
}

/**
 * A page named in the list: in the App's area a link to the page's own screen, in a site's frame a
 * link to the page on the site itself, the way the content inventory's View opens it. A page the
 * frame knows by its name alone is named without a link there. A page known by neither its name nor
 * its address is not named: its key is an identifier, not words.
 */
function PageName({
  inSiteFrame,
  pageKey,
  pageUrl,
  title,
  fullTitle,
  className,
}: {
  inSiteFrame: boolean;
  pageKey: string;
  pageUrl: string | undefined;
  title: string | undefined;
  fullTitle: string | undefined;
  className: string;
}) {
  if (inSiteFrame) {
    if (pageUrl) {
      return (
        <a href={pageUrl} target="_blank" rel="noreferrer" title={fullTitle || pageUrl} className={className}>
          {title || pageUrl}
          {/* The cue stays on the line of the name's last word. */}
          <span className="whitespace-nowrap">
            {NO_BREAK_SPACE}
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[0.625rem]" aria-hidden="true" />
          </span>
        </a>
      );
    }
    return <span className="font-medium text-slate-700">{title}</span>;
  }
  return (
    <Link to={brandPresenceContentItemPath({ key: pageKey })} title={fullTitle || pageUrl} className={className}>
      {title || pageUrl}
    </Link>
  );
}

/**
 * The project's issue list, as a triage list: each row says what is wrong, how urgent it is, which
 * page it is on and how urgent that page is, who fixes it and how long it has been open, holds one
 * status control, and offers its next steps: Optimize, where an optimization addresses the issue, and
 * Fix on the page's own Health tab, where the content team fixes it.
 *
 * A session whose token carries the write scope gets the status control, the checkboxes and the bulk
 * bar. Any other session sees the same rows and reads each status as a pill. Which controls render is
 * a rendering decision only; Sync Core refuses the write for a token without the scope either way.
 *
 * The rows arrive ordered by the server — priority first, most recently seen first within a
 * priority, unranked last — and the screen renders that order without sorting. The filters, the
 * page size and the grouped view live in the frame's own address, which the App mirrors.
 *
 * A site's frame shows the same list alone: a page opens on the site itself, and nothing offers a
 * way into the rest of the area, whose screens only the App hosts. Optimize there opens the page's own
 * Optimize screen on the site, where the site names one.
 */
export function GovernanceIssues() {
  const context = useContext(SyncCoreApiContext);
  const api = context?.api;
  const jwt = context?.jwt;
  const canChangeStatus = useMemo(() => getSessionScopes(jwt).includes(ISSUE_WRITE_OWN_SCOPE), [jwt]);
  const inSiteFrame = useContext(BrandPresenceSiteFrameContext);
  // The address the site hosting the frame opens a page's Optimize screen at, and the kinds of page it
  // has one for. A site that names none — one whose module predates it, or one that does not let this
  // person in there — offers no Optimize, and neither does a page of a kind it names no screen for.
  const appParams = useContext(AppParamsContext) as IAppParams | null;
  const requestedSiteOptimizeAddress: unknown = appParams?.pageOptions?.pageOptimizeUrl;
  const siteOptimizeAddress =
    inSiteFrame && typeof requestedSiteOptimizeAddress === 'string' && requestedSiteOptimizeAddress
      ? requestedSiteOptimizeAddress
      : undefined;
  const requestedSiteOptimizeEntityTypes: unknown = appParams?.pageOptions?.pageOptimizeEntityTypes;
  const siteOptimizeEntityTypes = useMemo(
    () =>
      Array.isArray(requestedSiteOptimizeEntityTypes)
        ? requestedSiteOptimizeEntityTypes.filter((entityType): entityType is string => typeof entityType === 'string')
        : [],
    [requestedSiteOptimizeEntityTypes]
  );
  const offersOptimize = !inSiteFrame || !!siteOptimizeAddress;

  const [searchParams, setSearchParams] = useSearchParams();
  const statusToken = searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.status);
  const issueTypeFilter = searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.issueType) ?? '';
  const responsibilityToken = searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.responsibility);
  const pageFilter = (searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.page) ?? '').trim();
  // A site's frame lists that site's own issues, which is all its token reaches, so there is no site to
  // choose between there and a site named in the address narrows nothing.
  const siteFilter = inSiteFrame ? '' : (searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.site) ?? '');
  const pageSize = parsePageSizeToken(searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.pageSize));
  const groupByPage = searchParams.get(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.view) === BRAND_PRESENCE_ISSUES_VIEW_BY_PAGE_TOKEN;

  // This screen keeps its view in the frame's own address, so in the App's area every change made
  // here is also sent to the host page with `update-query` — the way the other screens of the area report
  // theirs. Without the report the host page's address keeps the query it was handed last, and a
  // reload or a shared link reopens filters the reader has since changed or removed. The address
  // the screen opened with is already the host page's own and is not sent back.
  const screenQuery = useMemo(() => Object.fromEntries(searchParams) as Record<string, string>, [searchParams]);
  useReportBrandPresenceScreenQuery(screenQuery);
  const screenQuerySerialized = useMemo(() => JSON.stringify(screenQuery), [screenQuery]);
  const lastSentQuerySerializedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (lastSentQuerySerializedRef.current === screenQuerySerialized) {
      return;
    }
    const isOpeningAddress = lastSentQuerySerializedRef.current === undefined;
    lastSentQuerySerializedRef.current = screenQuerySerialized;
    // A site's page does not hand its address back to the frame, so the view stays the frame's own
    // there: an address the site's page keeps would reopen as the list's defaults under a query that
    // says otherwise.
    if (!isOpeningAddress && !inSiteFrame) {
      sendToParent({ type: 'update-query', query: screenQuery });
    }
  }, [screenQuery, screenQuerySerialized, inSiteFrame]);

  // The one address, read the way the other screens of the area read it: when the host page's
  // address moves on its own — its Back, a shared link — the screen takes the view it carries,
  // and a filter the address has dropped leaves the screen too. Only a change of that address is
  // taken, so a change made here is never undone while the host page catches up with it, and what
  // is taken is not sent back.
  const screenAddress = useBrandPresenceScreenAddress();
  const addressSearch = new URLSearchParams(searchOfBrandPresenceScreenAddress(screenAddress)).toString();
  useEffect(() => {
    const addressParams = new URLSearchParams(addressSearch);
    if (addressParams.toString() === searchParams.toString()) {
      return;
    }
    lastSentQuerySerializedRef.current = JSON.stringify(Object.fromEntries(addressParams));
    setSearchParams(addressParams, { replace: true });
  }, [addressSearch]);

  // The active optimization types, which say which issues an optimization can address: an issue whose type
  // none of them addresses offers no Optimize, as its check offers none on the page's Health tab. A read
  // that fails offers none either, and leaves the list as it is.
  const [optimizationTypes, setOptimizationTypes] = useState<ClientContentOptimizationTypeEntity[]>([]);
  useEffect(() => {
    if (!api || !offersOptimize) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const types = await loadActiveOptimizationTypes(api);
        if (!cancelled) {
          setOptimizationTypes(types);
        }
      } catch {
        // No issue offers Optimize.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, offersOptimize]);
  const optimizationTypeKeysByIssueType = useMemo(() => optimizationTypeKeysByIssueTypeKey(optimizationTypes), [optimizationTypes]);

  // A page is optimized once at a time, as on its Health tab. The list follows the optimization each page
  // was last given from here, by the page's key, with the issue whose row started it, so it outlives that
  // row: a row the grouped view moves, or one that leaves Open. While it is started or written, the page's
  // other rows wait.
  const [pageOptimizations, setPageOptimizations] = useState<Record<string, { issueId: string; started: IStartedOptimization }>>({});
  const [pageOptimizationViews, setPageOptimizationViews] = useState<Record<string, IOptimizationView>>({});
  const [startingIssueIdsByPage, setStartingIssueIdsByPage] = useState<Record<string, string[]>>({});
  const setIssueStarting = useCallback(({ pageKey, issueId, starting }: { pageKey: string; issueId: string; starting: boolean }) => {
    setStartingIssueIdsByPage((current) => {
      const ids = current[pageKey] ?? [];
      if (ids.includes(issueId) === starting) {
        return current;
      }
      return { ...current, [pageKey]: starting ? [...ids, issueId] : ids.filter((id) => id !== issueId) };
    });
  }, []);
  const followPageOptimization = useCallback(
    ({ pageKey, issueId, started }: { pageKey: string; issueId: string; started: IStartedOptimization }) => {
      setPageOptimizationViews((current) => withoutKey(current, pageKey));
      setPageOptimizations((current) => ({ ...current, [pageKey]: { issueId, started } }));
    },
    []
  );

  const [issues, setIssues] = useState<ClientIssueEntity[] | undefined>(undefined);
  const [tagsByKey, setTagsByKey] = useState<Record<string, ISyncCoreTaxonomyTerm>>({});
  const [pagesByKey, setPagesByKey] = useState<Record<string, ISyncCoreContentItem>>({});
  const [siteNamesById, setSiteNamesById] = useState<Record<string, string>>({});
  const [pageMeta, setPageMeta] = useState<IIssuePage | undefined>(undefined);
  // The status the list on screen was read for, which is what its count counts, while a read for
  // another filter is still out.
  const [listedStatus, setListedStatus] = useState<IssueStatus | undefined>(DEFAULT_STATUS);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  // Every issue type of the project, for the Issue type filter: the choice must not shrink to the
  // types on the loaded page, and it must not shrink to one type once a type is picked.
  const [issueTypeTerms, setIssueTypeTerms] = useState<{ key: string; name: string }[]>([]);
  const [issueTypeTermsRead, setIssueTypeTermsRead] = useState(false);
  useEffect(() => {
    if (!api) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const terms = await getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.IssueType);
        if (cancelled) {
          return;
        }
        setIssueTypeTerms(
          terms
            .map((term) => ({ key: String(term.key || '').trim(), name: String(term.name || '').trim() }))
            .filter((term) => term.key.length > 0)
        );
        setIssueTypeTermsRead(true);
      } catch {
        // Without the project's own list the filter still offers the types on the loaded page.
        if (cancelled) {
          return;
        }
        setIssueTypeTerms([]);
        setIssueTypeTermsRead(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  // The sites the Site filter offers, and the count that decides whether it appears at all.
  const [sites, setSites] = useState<ClientSiteEntity[]>([]);
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const [siteCount, setSiteCount] = useState(0);
  // A link can name any site by id, and only the site itself says what it is. The named site is read
  // once: a site a customer connected joins the options under its own name, while the platform's own
  // source of terms, and a site this session cannot read, narrow nothing and are offered as nothing.
  const [linkedSite, setLinkedSite] = useState<ClientSiteEntity | undefined>(undefined);
  const [rejectedSiteId, setRejectedSiteId] = useState<string | undefined>(undefined);

  // A status change updates a row before the server confirms it, may remove a row that no longer
  // matches the active filter, keeps the row's control waiting while the write is in flight, and states
  // any failure beside the row it belongs to without discarding the list.
  const [statusOverrides, setStatusOverrides] = useState<Record<string, IssueStatus>>({});
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  // A read answers with what Sync Core held when it was asked. A row whose write is still out keeps
  // what it shows while it waits, and a read asked before a write answered is dropped: the read the
  // answer asks for carries it.
  const pendingIdsRef = useRef(pendingIds);
  pendingIdsRef.current = pendingIds;
  const answeredWritesRef = useRef(0);
  // Rows the server reported as already closed; they offer no further change until the next read.
  const [closedElsewhereIds, setClosedElsewhereIds] = useState<Set<string>>(new Set());
  const [announcement, setAnnouncement] = useState<string | undefined>(undefined);

  // The rows whose "Why this was flagged" section is open.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // The rows picked for a bulk decision, and the decision being confirmed for them.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDraft, setBulkDraft] = useState<IBulkDraft | undefined>(undefined);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState<string | undefined>(undefined);

  // The verdict dialog: the row it is open for and the reason typed so far.
  const [verdictDraft, setVerdictDraft] = useState<IVerdictDraft | undefined>(undefined);
  const [verdictOpen, setVerdictOpen] = useState(false);
  const [verdictNote, setVerdictNote] = useState('');
  // Each verdict gets a number of its own when it opens. A row keeps its control while its own write
  // is in flight, so a second verdict can be opened on another row before the first has answered;
  // both halves of a settlement carry the number of the verdict they belong to, so the first row's
  // answer can never stamp an outcome onto the verdict now on screen and pull focus off it.
  const verdictTokenRef = useRef(0);
  // A confirmed verdict takes the row's control away, so focus lands on the table region. Two things
  // have to have happened before it can: the write has to have answered, and the dialog has to have
  // finished closing — the dialog restores focus itself as it goes, after its leave animation, so a
  // move made before that restore is undone by it. Whichever of the two lands last does the move,
  // and only when both belong to the same verdict.
  const verdictCloseFinishedRef = useRef<number | undefined>(undefined);
  const verdictOutcomeRef = useRef<{ token: number; outcome: IStatusChangeOutcome } | undefined>(undefined);
  // The verdict whose dialog is on its way out, read by the close hook once the dialog has gone; a
  // verdict opened before that read clears it, because the dialog on screen belongs to the new one.
  const verdictClosingTokenRef = useRef<number | undefined>(undefined);
  // The control the dialog was opened from, which the dialog's own restore puts focus back on.
  const verdictTriggerRef = useRef<Element | null>(null);
  // The bulk bar's button the bulk confirmation was opened from.
  const bulkTriggerRef = useRef<Element | null>(null);
  // Where focus goes once a change has taken away the control that held it.
  const tableRegionRef = useRef<HTMLDivElement>(null);
  const [focusTableIfLost, setFocusTableIfLost] = useState(false);
  const focusTableIfLostNow = useCallback(() => {
    if (!document.activeElement || document.activeElement === document.body) {
      tableRegionRef.current?.focus();
    }
  }, []);
  // The bulk confirmation has gone and has restored focus. Where the bar's button it was opened from is
  // still on screen, focus is back on it; where the bar left with the selection, the restore fell back on
  // something else, and the table takes focus, as it does after a row's own change.
  const bulkClosed = useCallback(() => {
    const trigger = bulkTriggerRef.current;
    bulkTriggerRef.current = null;
    if (!trigger || !trigger.isConnected) {
      tableRegionRef.current?.focus();
    }
  }, []);
  useEffect(() => {
    if (!focusTableIfLost) {
      return;
    }
    setFocusTableIfLost(false);
    focusTableIfLostNow();
  }, [focusTableIfLost, focusTableIfLostNow]);

  const filters = useMemo<IIssueListFilters>(() => {
    const next: IIssueListFilters = { order: ISSUE_ORDER };
    const status = statusFromToken(statusToken);
    if (status) {
      next.status = status;
    }
    if (issueTypeFilter) {
      next.issueType = issueTypeFilter;
    }
    const responsibility = responsibilityFromToken(responsibilityToken);
    if (responsibility) {
      next.responsibility = responsibility;
    }
    if (pageFilter) {
      next.contentItemKey = pageFilter;
    }
    if (siteFilter && rejectedSiteId !== siteFilter) {
      // The value is the site's own id, carried through the address as text.
      next.site = siteFilter as InternalId;
    }
    return next;
  }, [statusToken, issueTypeFilter, responsibilityToken, pageFilter, siteFilter, rejectedSiteId]);

  // The filters as they stand on screen, for a write that answers after they changed: the list the
  // answer lands in is the one the person is looking at, not the one the write was started under.
  // The assignment belongs in the render that derives them, not in an effect afterwards: an answer
  // landing between the two would still read the filters the screen has already left.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // A page number belongs to the filters and the page size it was turned for, so a change of either
  // takes the screen back to the first page in the very derivation that changed it.
  const filtersKey = `${JSON.stringify(filters)}:${pageSize}`;
  const [pagedFiltersKey, setPagedFiltersKey] = useState(filtersKey);
  if (pagedFiltersKey !== filtersKey) {
    setPagedFiltersKey(filtersKey);
    setPage(0);
  }

  useEffect(() => {
    // A site's frame offers no Site filter, so it reads no site list.
    if (!api || inSiteFrame) {
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        // Every page, so the filter offers every site the project has and the count it is shown
        // beside counts the same sites.
        const loaded: ClientSiteEntity[] = [];
        let total = 0;
        for (let sitePage = 0; sitePage < SITE_OPTIONS_MAX_PAGES; sitePage += 1) {
          const response = await api.billing.sites.list({}, { page: sitePage, itemsPerPage: SITE_OPTIONS_PAGE_SIZE });
          if (cancelled) {
            return;
          }
          loaded.push(...(response.items ?? []));
          total = response.totalNumberOfItems ?? loaded.length;
          if (sitePage + 1 >= (response.numberOfPages ?? 1)) {
            break;
          }
        }
        // The filter offers the sites a customer connected, and the count beside it counts those same
        // sites, the way the site picker and the project's site lookup read the very same list: the
        // platform's own source of terms is neither offered nor counted, whatever the list hands over.
        const offered = onlyRegularSites(loaded);
        setSites(offered);
        setSitesLoaded(true);
        setSiteCount(total - (loaded.length - offered.length));
      } catch {
        // Without the site list the filter cannot narrow anything, so it stays hidden and the issue
        // list keeps working.
        if (cancelled) {
          return;
        }
        setSites([]);
        setSitesLoaded(true);
        setSiteCount(0);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, inSiteFrame]);

  useEffect(() => {
    // A site the list already offers has said what it is, so only an id from somewhere else is read,
    // and the read waits for the list rather than racing it.
    if (!api || !siteFilter || !sitesLoaded || sites.some((site) => site.id === siteFilter)) {
      setLinkedSite(undefined);
      setRejectedSiteId(undefined);
      return;
    }

    let cancelled = false;
    void (async () => {
      const site = await api.billing.sites.item(siteFilter as InternalId).catch(() => undefined);
      if (cancelled) {
        return;
      }
      if (site && !isPlatformGovernanceSite(site)) {
        setLinkedSite(site);
        setRejectedSiteId(undefined);
        return;
      }
      setLinkedSite(undefined);
      setRejectedSiteId(siteFilter);
    })();

    return () => {
      cancelled = true;
    };
  }, [api, siteFilter, sites, sitesLoaded]);

  useEffect(() => {
    if (!api) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    const answeredWritesWhenAsked = answeredWritesRef.current;

    void (async () => {
      try {
        const response = await api.governance.issues.list({ page, itemsPerPage: pageSize }, filters);
        const [tags, pages, siteNames] = await Promise.all([
          resolveTagsByKey(response.items),
          resolvePagesByKey(response.items),
          resolveSiteNamesById(response.items),
        ]);
        // A write that answered since this read was asked is not in its answer, and has asked for a
        // read of its own, whose answer is the one the list takes.
        if (cancelled || answeredWritesRef.current !== answeredWritesWhenAsked) {
          return;
        }
        const listed = new Set<string>(response.items.map((issue) => issue.id));
        const waiting = pendingIdsRef.current;
        setIssues(response.items);
        setTagsByKey(tags);
        setPagesByKey(pages);
        setSiteNamesById(siteNames);
        setPageMeta({ page: response.page, numberOfPages: response.numberOfPages, totalNumberOfItems: response.totalNumberOfItems });
        setStatusOverrides((current) => keepEntries({ record: current, keep: (id) => waiting.has(id) }));
        setRemovedIds(new Set());
        setRowErrors((current) => keepEntries({ record: current, keep: (id) => listed.has(id) }));
        setClosedElsewhereIds(new Set());
        setExpandedIds((current) => new Set(Array.from(current).filter((id) => listed.has(id))));
        setSelectedIds((current) => new Set(Array.from(current).filter((id) => listed.has(id))));
        setListedStatus(filters.status);
        setFailed(false);
      } catch {
        // A failed read keeps the rows already on screen and offers a retry, rather than blanking a
        // list the reader was working through. A read dropped for a write that answered is not the
        // list's: the read that write asked for is still out.
        if (cancelled || answeredWritesRef.current !== answeredWritesWhenAsked) {
          return;
        }
        setFailed(true);
      } finally {
        // A read dropped for a write that answered leaves the list loading: the read that write asked
        // for is still out.
        if (!cancelled && answeredWritesRef.current === answeredWritesWhenAsked) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, filters, page, pageSize, reloadToken]);

  /** Sets or removes several parameters of the address at once; a value that is empty leaves it. */
  const updateParams = useCallback(
    (changes: Record<string, string | undefined>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // A site the list is not narrowed by leaves the address too, so the chips claim no filter that is
  // not applied. The screen drops it on its own, so the drop is not reported: a report would be an
  // entry in the host page's history that a Back lands on only to find the site refused again.
  useEffect(() => {
    if (siteFilter && rejectedSiteId === siteFilter) {
      const next = new URLSearchParams(searchParams);
      next.delete(BRAND_PRESENCE_ISSUES_QUERY_PARAMS.site);
      lastSentQuerySerializedRef.current = JSON.stringify(Object.fromEntries(next));
      setSearchParams(next, { replace: true });
    }
  }, [siteFilter, rejectedSiteId, searchParams, setSearchParams]);

  /** Every filter back to its default; the page size and the view are the reader's and stay. */
  const clearFilters = useCallback(() => {
    updateParams({
      [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.status]: undefined,
      [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.issueType]: undefined,
      [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.responsibility]: undefined,
      [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.page]: undefined,
      [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.site]: undefined,
    });
  }, [updateParams]);

  const changeFilters = useCallback(
    (values: IIssuesFilterValues) => {
      updateParams({
        // The status the list opens on stays out of the address, as every value at its default does.
        [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.status]:
          values.statusToken === BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS[DEFAULT_STATUS] ? undefined : values.statusToken,
        [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.issueType]: values.issueTypeKey,
        [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.responsibility]: values.responsibilityToken,
        [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.page]: values.pageKey,
        [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.site]: inSiteFrame ? undefined : values.siteId,
      });
    },
    [updateParams, inSiteFrame]
  );

  const retry = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const changeStatus = useCallback(
    async (
      issue: ClientIssueEntity,
      next: IssueStatus,
      options?: { note?: string; done?: string; pending?: string; optimistic?: boolean }
    ): Promise<IStatusChangeOutcome> => {
      if (!api) {
        return 'failed';
      }
      const previous = statusOverrides[issue.id] ?? issue.status;

      setRowErrors((current) => withoutKey(current, issue.id));
      // The write is in flight from here: the caller's pending words replace whatever the region last
      // said, so a control that stays reachable while it waits is not a control that waits in silence.
      setAnnouncement(options?.pending);
      if (options?.optimistic ?? true) {
        setStatusOverrides((current) => ({ ...current, [issue.id]: next }));
      }
      setPendingIds((current) => new Set(current).add(issue.id));

      const request: IPatchIssueStatusRequest = options?.note ? { status: next, note: options.note } : { status: next };
      try {
        const updated = await api.governance.issues.patchStatus(issue.id, request);
        const confirmed = updated.status;
        setStatusOverrides((current) => ({ ...current, [issue.id]: confirmed }));
        // A row that no longer matches the active status filter leaves the list, and the count with
        // it. The filter this is decided against is the one on screen when the answer lands: a filter
        // changed while the write was out has already been read for, and the row belongs where that
        // read puts it.
        const activeStatus = filtersRef.current.status;
        if (activeStatus && confirmed !== activeStatus) {
          setRemovedIds((current) => new Set(current).add(issue.id));
        }
        setAnnouncement(options?.done ?? `Status changed to ${issueStatusLabel(confirmed)}.`);
        // The page is read again from Sync Core: the rows behind the one that left move up onto it, and
        // the count and the pages are Sync Core's own. It is loading from here, not empty.
        answeredWritesRef.current += 1;
        setLoading(true);
        setReloadToken((token) => token + 1);
        return 'changed';
      } catch (error) {
        // Put the row back where it was and say what failed beside it, leaving every other row untouched.
        setStatusOverrides((current) => (previous === issue.status ? withoutKey(current, issue.id) : { ...current, [issue.id]: previous }));
        const status = clientErrorStatus(error);
        const message = refusalCopy(status);
        setRowErrors((current) => ({ ...current, [issue.id]: message }));
        setAnnouncement(message);
        if (status === HTTP_CONFLICT) {
          // Someone closed the issue elsewhere, so the list Sync Core holds is not the one on screen:
          // the row offers nothing more, and the page is read again, which shows the row as Sync Core
          // holds it, or takes it off with the count and the pages.
          setClosedElsewhereIds((current) => new Set(current).add(issue.id));
          answeredWritesRef.current += 1;
          setReloadToken((token) => token + 1);
          return 'already-closed';
        }
        return 'failed';
      } finally {
        setPendingIds((current) => {
          const withoutRow = new Set(current);
          withoutRow.delete(issue.id);
          return withoutRow;
        });
        setFocusTableIfLost(true);
      }
    },
    [api, statusOverrides]
  );

  /**
   * Put focus where a finished verdict leaves it — but only once both halves of that same verdict
   * have landed: its write has answered and its dialog has finished closing. Called from each half
   * with the verdict's own number, it does nothing until the other half of that same verdict has been
   * there too, so the dialog's own focus restore never lands after this and an older verdict's answer
   * never settles the one a person is looking at.
   */
  const settleVerdictFocus = useCallback((token: number) => {
    const stored = verdictOutcomeRef.current;
    if (token !== verdictTokenRef.current || verdictCloseFinishedRef.current !== token || stored?.token !== token) {
      return;
    }
    verdictCloseFinishedRef.current = undefined;
    verdictOutcomeRef.current = undefined;
    verdictClosingTokenRef.current = undefined;
    const trigger = verdictTriggerRef.current;
    verdictTriggerRef.current = null;
    if (stored.outcome === 'failed') {
      // The row kept its control, and the restore has already handed focus back to it. That is
      // where it belongs.
      return;
    }
    // An issue the server reports as already closed settles the way a written one does: the row is
    // told what happened and gives up its control with it, so the control the restore returns to is
    // on its way out either way.
    // Focus the person has moved somewhere of their own since stays where they put it. The control
    // the restore returns to is not such a move, and neither is a row that left with its control.
    const active = document.activeElement;
    const movedOn = active instanceof HTMLElement && active !== document.body && active !== trigger;
    if (!movedOn) {
      tableRegionRef.current?.focus();
    }
  }, []);

  const openVerdict = useCallback((draft: IVerdictDraft) => {
    verdictTokenRef.current += 1;
    verdictCloseFinishedRef.current = undefined;
    verdictOutcomeRef.current = undefined;
    verdictClosingTokenRef.current = undefined;
    verdictTriggerRef.current = document.activeElement;
    setVerdictDraft(draft);
    setVerdictNote('');
    setVerdictOpen(true);
  }, []);

  const closeVerdict = useCallback(() => {
    verdictClosingTokenRef.current = verdictTokenRef.current;
    setVerdictOpen(false);
  }, []);

  /** The dialog has gone and has restored focus; whatever that verdict decided may now take it. */
  const verdictClosed = useCallback(() => {
    const token = verdictClosingTokenRef.current;
    if (token === undefined) {
      return;
    }
    verdictCloseFinishedRef.current = token;
    settleVerdictFocus(token);
  }, [settleVerdictFocus]);

  const confirmVerdict = useCallback(async () => {
    if (!verdictDraft || !verdictOpen) {
      return;
    }
    const token = verdictTokenRef.current;
    verdictClosingTokenRef.current = token;
    setVerdictOpen(false);
    const note = verdictNote.trim();
    const label = issueLabelOf(verdictDraft.tagName);
    // The row keeps its control until the server answers, so a refusal hands focus back to the control
    // that opened the dialog; once the row is closed that control is gone and focus goes to the table.
    const outcome = await changeStatus(verdictDraft.issue, verdictDraft.verdict.decision.status, {
      note: note || undefined,
      done: verdictDraft.verdict.decision.doneOne,
      pending: savingStatusCopy(label),
      optimistic: false,
    });
    if (token !== verdictTokenRef.current) {
      // A verdict opened on another row while this write was in flight and the dialog is its own
      // now. Its two halves settle its focus; this answer says only what happened to its own row,
      // which the row's copy and the live region have already said.
      return;
    }
    verdictOutcomeRef.current = { token, outcome };
    settleVerdictFocus(token);
  }, [verdictDraft, verdictOpen, verdictNote, changeStatus, settleVerdictFocus]);

  /** One status chosen in a row's control: a dialog for the two verdicts, a direct write otherwise. */
  const onStatusChoice = useCallback(
    ({ issue, tagName, choiceId }: { issue: ClientIssueEntity; tagName: string | undefined; choiceId: string }) => {
      const tagLabel = issueLabelOf(tagName);
      if (choiceId === REOPEN_DECISION.id) {
        void changeStatus(issue, REOPEN_DECISION.status, { done: REOPEN_DECISION.doneOne, pending: savingStatusCopy(tagLabel) });
        return;
      }
      const decision = ISSUE_DECISIONS.find((candidate) => candidate.id === choiceId);
      if (!decision) {
        return;
      }
      const verdict = verdictOf(decision);
      if (verdict) {
        openVerdict({ issue, verdict, tagName });
        return;
      }
      void changeStatus(issue, decision.status, { done: decision.doneOne, pending: savingStatusCopy(tagLabel) });
    },
    [changeStatus, openVerdict]
  );

  /**
   * The one confirmation of a bulk decision: one shared, optional reason for every picked row. A row
   * already at the decided status is not written again.
   */
  const confirmBulk = useCallback(async () => {
    if (!api || !bulkDraft || bulkPending) {
      return;
    }
    setBulkPending(true);
    const note = bulkNote.trim();
    const request: IPatchIssueStatusRequest = note ? { status: bulkDraft.decision.status, note } : { status: bulkDraft.decision.status };
    const toWrite = bulkDraft.issues.filter((issue) => (statusOverrides[issue.id] ?? issue.status) !== bulkDraft.decision.status);
    const writing = new Set(toWrite.map((issue) => String(issue.id)));
    setRowErrors((current) => keepEntries({ record: current, keep: (id) => !writing.has(id) }));
    const results = await Promise.allSettled(toWrite.map((issue) => api.governance.issues.patchStatus(issue.id, request)));
    // Each answer is taken the way a row's own change takes it: the row shows the status Sync Core
    // confirmed and leaves where that no longer matches the status filter on screen, a row someone else
    // closed says so and offers nothing more, and a refusal says why beside its row, which stays picked.
    const activeStatus = filtersRef.current.status;
    const confirmed: Record<string, IssueStatus> = {};
    const leaving = new Set<string>();
    const closedElsewhere = new Set<string>();
    const refused: Record<string, string> = {};
    const retryable = new Set<string>();
    let deniedCount = 0;
    let failedCount = 0;
    results.forEach((result, index) => {
      const id = String(toWrite[index].id);
      if (result.status === 'fulfilled') {
        confirmed[id] = result.value.status;
        if (activeStatus && result.value.status !== activeStatus) {
          leaving.add(id);
        }
        return;
      }
      const status = clientErrorStatus(result.reason);
      refused[id] = refusalCopy(status);
      if (status === HTTP_CONFLICT) {
        closedElsewhere.add(id);
      } else if (status === HTTP_FORBIDDEN) {
        deniedCount += 1;
      } else {
        failedCount += 1;
        retryable.add(id);
      }
    });
    setBulkPending(false);
    setBulkOpen(false);
    setStatusOverrides((current) => ({ ...current, ...confirmed }));
    if (leaving.size > 0) {
      setRemovedIds((current) => new Set([...Array.from(current), ...Array.from(leaving)]));
    }
    if (closedElsewhere.size > 0) {
      setClosedElsewhereIds((current) => new Set([...Array.from(current), ...Array.from(closedElsewhere)]));
    }
    setRowErrors((current) => ({ ...current, ...refused }));
    const total = bulkDraft.issues.length;
    const doneCount = total - failedCount - deniedCount - closedElsewhere.size;
    // What went wrong stays on screen; what was done is said, beside it or on its own.
    const problems: string[] = [];
    if (failedCount > 0) {
      problems.push(bulkFailedCopy({ failed: failedCount + deniedCount, total }));
    }
    if (deniedCount > 0) {
      problems.push(PERMISSION_DENIED_COPY);
    }
    if (closedElsewhere.size > 0) {
      problems.push(bulkAlreadyClosedCopy(closedElsewhere.size));
    }
    const done = doneCount > 0 && failedCount === 0 ? [bulkDoneCopy(bulkDraft.decision, doneCount)] : [];
    setBulkError(problems.length > 0 ? problems.join(' ') : undefined);
    setAnnouncement([...done, ...problems].join(' '));
    // A row a write could not change for a reason another try can lift stays picked, so the decision can
    // be tried again on it alone.
    setSelectedIds(retryable);
    // The list is read again either way: the rows behind the ones that left move up, and every row
    // shows the status the server holds. A read asked before the writes answered is dropped, and the
    // list is loading from here, not empty.
    answeredWritesRef.current += 1;
    setLoading(true);
    setReloadToken((token) => token + 1);
  }, [api, bulkDraft, bulkNote, bulkPending, statusOverrides]);

  const statusFilter = statusFromToken(statusToken);

  // The Issue type options are every type of the project, joined by any tag the loaded rows name. Each
  // is offered by its name; a type whose name could not be read is not offered, because its key is an
  // identifier, and a key the address carries narrows the list all the same.
  const issueTypeOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const term of issueTypeTerms) {
      if (term.name) {
        byKey.set(term.key, term.name);
      }
    }
    // A tag a loaded row names joins the options under its own name only.
    for (const issue of issues ?? []) {
      const key = issue.issueType.getStableKey();
      const name = tagsByKey[key]?.name;
      if (key && name && !byKey.has(key)) {
        byKey.set(key, name);
      }
    }
    return Array.from(byKey);
  }, [issueTypeTerms, issues, tagsByKey]);

  // The Site options are the project's sites, plus the site a link named once it turned out to be one
  // a customer connected. Every option carries the site's own name; nothing here shows an id.
  const siteOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const site of sites) {
      byId.set(site.id, site.name);
    }
    if (linkedSite && !byId.has(linkedSite.id)) {
      byId.set(linkedSite.id, linkedSite.name);
    }
    return Array.from(byId);
  }, [sites, linkedSite]);

  // The filter is worth showing when there is something to choose between, and it stays on screen
  // while a site is narrowing the list even if the project has only that one — otherwise a link that
  // named a site would narrow the list with no control on screen to widen it again.
  const narrowedBySite = !!siteFilter && rejectedSiteId !== siteFilter;
  const hasMultipleSites = !inSiteFrame && (siteCount > 1 || narrowedBySite);

  // The filters as the list reads them, not as the address spells them: a token the list does not know
  // narrows nothing, so it offers nothing to clear.
  const hasActiveFilters =
    !!(issueTypeFilter || responsibilityFromToken(responsibilityToken) || pageFilter || narrowedBySite) || statusFilter !== DEFAULT_STATUS;

  const visibleIssues = useMemo(() => (issues ?? []).filter((issue) => !removedIds.has(issue.id)), [issues, removedIds]);
  // No result yet and no error means the first read is still in flight, so the table shows its
  // skeleton rather than flashing the empty state before the request even starts.
  const isInitialLoading = issues === undefined && !failed;
  const isReloading = loading && issues !== undefined;
  const hasRows = visibleIssues.length > 0;
  // A later page that answered empty while issues remain steps back to the last page with issues; until
  // that page is read the list is on its way there, not empty.
  const steppingBack = !hasRows && !failed && !!pageMeta && pageMeta.totalNumberOfItems > 0 && page > 0;
  // The count is Sync Core's. A row a change took off leaves it at once, until the page is read again
  // and Sync Core counts it; a first page that answers no row counts nothing, whatever count came with it.
  const removedVisibleCount = (issues?.length ?? 0) - visibleIssues.length;
  const displayedCount =
    pageMeta && (hasRows || loading || steppingBack) ? Math.max(0, pageMeta.totalNumberOfItems - removedVisibleCount) : 0;
  const countLabel = pageMeta && !isInitialLoading ? issueCountLabel(displayedCount, listedStatus) : '';

  // A row a decision can still change: its status is one Sync Core lets go of, the server has not
  // already reported it closed, and no change of its own is still out. Only such a row can be picked
  // for a bulk decision.
  const isChangeable = useCallback(
    (issue: ClientIssueEntity) =>
      !isTerminalStatus(statusOverrides[issue.id] ?? issue.status) && !closedElsewhereIds.has(issue.id) && !pendingIds.has(issue.id),
    [statusOverrides, closedElsewhereIds, pendingIds]
  );
  const selectableIssues = useMemo(() => visibleIssues.filter(isChangeable), [visibleIssues, isChangeable]);
  const selectedIssues = useMemo(() => selectableIssues.filter((issue) => selectedIds.has(issue.id)), [selectableIssues, selectedIds]);
  const allSelected = selectableIssues.length > 0 && selectableIssues.every((issue) => selectedIds.has(issue.id));
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((current) => {
      if (selectableIssues.length > 0 && selectableIssues.every((issue) => current.has(issue.id))) {
        return new Set();
      }
      return new Set(selectableIssues.map((issue) => issue.id));
    });
  }, [selectableIssues]);
  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // A read that answers a page past the last one that has issues - the list shrank since the pages were
  // counted, here or elsewhere - steps back to the last page Sync Core says has issues, rather than leaving the
  // reader on an empty page with no way back. Each answer steps back once: the render that follows
  // the step still holds the same answer.
  const steppedBackReadRef = useRef<ClientIssueEntity[] | undefined>(undefined);
  useEffect(() => {
    if (
      !issues ||
      issues.length > 0 ||
      loading ||
      failed ||
      !pageMeta ||
      pageMeta.totalNumberOfItems <= 0 ||
      page <= 0 ||
      steppedBackReadRef.current === issues
    ) {
      return;
    }
    steppedBackReadRef.current = issues;
    // The page stepped back to is read in the render after this one; until then it is loading, not empty.
    setLoading(true);
    setPage(Math.max(0, Math.min(page - 1, pageMeta.numberOfPages - 1)));
  }, [issues, loading, failed, pageMeta, page]);

  // Where a row of a site's frame opens its page's Optimize screen on that site: an issue an optimization
  // addresses, on a page the site named, of a kind the site names the screen for.
  const siteOptimizeHrefForIssue = useCallback(
    (issue: ClientIssueEntity): string | undefined => {
      if (!siteOptimizeAddress) {
        return undefined;
      }
      const typeKeys = optimizationTypeKeysByIssueType[issue.issueType.getStableKey()] ?? [];
      if (!optimizationTypesByKeys({ types: optimizationTypes, typeKeys }).length) {
        return undefined;
      }
      const pageKey = issue.contentItem?.getStableKey();
      const entity = pageKey ? pagesByKey[pageKey]?.entityReference : undefined;
      return entity && siteOptimizeEntityTypes.includes(entity.entityTypeNamespaceMachineName)
        ? siteOptimizeHrefOf({ address: siteOptimizeAddress, entity })
        : undefined;
    },
    [siteOptimizeAddress, optimizationTypeKeysByIssueType, optimizationTypes, pagesByKey, siteOptimizeEntityTypes]
  );

  // The columns of the table: the grouped view names each page once above its rows, so it has no
  // Page column of its own. A site's frame offers no next step of the App's; it has an Actions column for
  // Optimize where the site names its own Optimize screen, from the skeleton on, so the list keeps its
  // shape while the rows and what they offer arrive.
  const showPageColumn = !groupByPage;
  const showActionsColumn = !inSiteFrame || !!siteOptimizeAddress;
  const columnCount = 6 + (showPageColumn ? 1 : 0) + (showActionsColumn ? 1 : 0);

  /** A page's title as a row names it: without its site's own name, where the title carries it. */
  const pageTitleOf = useCallback(
    (resolvedPage: ISyncCoreContentItem | undefined, issue: ClientIssueEntity | undefined): string | undefined => {
      const title = resolvedPage?.name?.trim();
      if (!title) {
        return undefined;
      }
      const siteId = issue?.site ? String(issue.site.getId()) : undefined;
      return withoutSiteName({ title, siteName: siteId ? siteNamesById[siteId] : undefined });
    },
    [siteNamesById]
  );

  // The name a picked page's chip carries: its whole title, as the Page filter offered it, remembered
  // once a listed row has named it, so a filter that leaves the page without issues keeps the name.
  const [pageLabelByKey, setPageLabelByKey] = useState<Record<string, string>>({});
  const listedPageLabel = pageFilter ? pagesByKey[pageFilter]?.name?.trim() || pagesByKey[pageFilter]?.url : undefined;
  useEffect(() => {
    if (pageFilter && listedPageLabel) {
      setPageLabelByKey((current) => (current[pageFilter] === listedPageLabel ? current : { ...current, [pageFilter]: listedPageLabel }));
    }
  }, [pageFilter, listedPageLabel]);
  const pageLabelForChip = pageFilter ? (pageLabelByKey[pageFilter] ?? listedPageLabel) : undefined;
  // The page whose own read by key has answered, named or not.
  const [pageReadByKey, setPageReadByKey] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!api || !pageFilter || pageLabelForChip) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const page = await api.governance.contentItems.itemByKey(pageFilter);
        const label = page?.name?.trim() || page?.url;
        if (!cancelled && label) {
          setPageLabelByKey((current) => ({ ...current, [pageFilter]: label }));
        }
      } catch {
        // The chip stays unnamed rather than show the page's key.
      } finally {
        if (!cancelled) {
          setPageReadByKey(pageFilter);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, pageFilter, pageLabelForChip]);

  // A chip's value still being named: the list that may name it, or the value's own read, is out. A site the
  // list does not offer is read on its own until it is offered or refused.
  const listRead = !loading && (issues !== undefined || failed);
  const namesLoading = useMemo<IIssuesFilterNamesLoading>(
    () => ({
      issueType: !!issueTypeFilter && !issueTypeOptions.some(([key]) => key === issueTypeFilter) && (!issueTypeTermsRead || !listRead),
      page: !!pageFilter && !pageLabelForChip && (!listRead || pageReadByKey !== pageFilter),
      site: !!siteFilter && !siteOptions.some(([id]) => id === siteFilter) && rejectedSiteId !== siteFilter,
    }),
    [
      issueTypeFilter,
      issueTypeOptions,
      issueTypeTermsRead,
      listRead,
      pageFilter,
      pageLabelForChip,
      pageReadByKey,
      siteFilter,
      siteOptions,
      rejectedSiteId,
    ]
  );

  // The grouped view: each page once, in the order the server ranked its first issue, its issues
  // beneath it; issues without a page gather under a heading that says so, where their first one fell.
  const groupedByPage = useMemo(() => {
    if (!groupByPage) {
      return [];
    }
    const groups = new Map<string, ClientIssueEntity[]>();
    for (const issue of visibleIssues) {
      const key = issue.contentItem?.getStableKey() || '';
      const existing = groups.get(key);
      if (existing) {
        existing.push(issue);
      } else {
        groups.set(key, [issue]);
      }
    }
    return Array.from(groups, ([pageKey, groupIssues]) => ({ pageKey, issues: groupIssues }));
  }, [groupByPage, visibleIssues]);

  const now = Date.now();

  /** One issue row, with its expanded reason beneath it when open. */
  const renderIssueRow = (issue: ClientIssueEntity) => {
    // The tag and the page a row names are read from the lookups beside the entities, under the key
    // the issue's own reference carries.
    const tagName = tagsByKey[issue.issueType.getStableKey()]?.name;
    const tagLabel = issueLabelOf(tagName);
    const pageKey = issue.contentItem?.getStableKey();
    const resolvedPage = pageKey ? pagesByKey[pageKey] : undefined;
    const pageAddress = resolvedPage?.url;
    const pageTitle = pageTitleOf(resolvedPage, issue);
    const displayedStatus = statusOverrides[issue.id] ?? issue.status;
    const terminal = isTerminalStatus(displayedStatus);
    const closedElsewhere = closedElsewhereIds.has(issue.id);
    const changeable = canChangeStatus && !terminal && !closedElsewhere;
    const pending = pendingIds.has(issue.id);
    const rowError = rowErrors[issue.id];
    const healthCheckKey = issue.healthCheck?.getStableKey();
    const expanded = expandedIds.has(issue.id);
    const reasonPanelId = `issue-reason-${issue.id}`;
    const resolvedOn = formatRowDate(issue.resolvedAt);
    const openedOn = formatRowDate(issue.firstDetectedAt);
    const openDays = daysSince(issue.firstDetectedAt, now);
    const lastSeenOn = displayedStatus === IssueStatus.Fixed ? formatRowDate(issue.lastDetectedAt) : undefined;
    const note = issue.note?.trim();
    const contentTeamFixes = issue.resolutionResponsibility === IssueResolutionResponsibility.Content;
    // The optimization types that address the issue's type, which the page's Health tab starts its
    // optimization with; none, and the row offers no Optimize.
    const optimizationTypesOfIssue = optimizationTypesByKeys({
      types: optimizationTypes,
      typeKeys: optimizationTypeKeysByIssueType[issue.issueType.getStableKey()] ?? [],
    });
    const siteOptimizeHref = inSiteFrame ? siteOptimizeHrefForIssue(issue) : undefined;

    return (
      <React.Fragment key={issue.id}>
        <tr className={`align-middle transition-colors duration-150 hover:bg-slate-50/80 ${isReloading ? 'opacity-60' : ''}`}>
          <td className="py-3.5 pl-4 pr-1">
            <div className="flex items-center gap-1.5">
              {isChangeable(issue) && canChangeStatus ? (
                <Form.Check
                  type="checkbox"
                  className="mb-0"
                  checked={selectedIds.has(issue.id)}
                  onChange={() => toggleSelected(issue.id)}
                  aria-label={`Select ${tagLabel}`}
                />
              ) : canChangeStatus ? (
                // Keeps the expander where the rows with a checkbox have it.
                <span className="inline-block w-4" aria-hidden="true" />
              ) : null}
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-label={`Why this was flagged: ${tagLabel}`}
                aria-expanded={expanded}
                aria-controls={reasonPanelId}
                onClick={() => toggleExpanded(issue.id)}
              >
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-xs transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </td>
          <td className={CELL_CLASSES}>
            {changeable ? (
              <IssueStatusControl
                label={`Status of ${tagLabel}: ${issueStatusLabel(displayedStatus)}`}
                status={displayedStatus}
                choices={statusChoicesFor(displayedStatus)}
                waiting={pending}
                onChoose={(choiceId) => onStatusChoice({ issue, tagName, choiceId })}
                onChoiceWhileWaiting={() => setAnnouncement(refusedWhileSavingCopy(tagLabel))}
              />
            ) : // Where the list on screen was read for one status every row would repeat it, so the pill shows
            // only on a list read for any status.
            listedStatus ? null : (
              <IssueStatusPill status={displayedStatus} title={canChangeStatus && terminal ? TERMINAL_TITLE : undefined} />
            )}
            {displayedStatus !== IssueStatus.Open && resolvedOn ? (
              <div className="mt-1 text-xs text-slate-500" title={issue.resolvedAt?.format(ROW_DATE_FULL_FORMAT)}>
                {issueStatusLabel(displayedStatus)} {resolvedOn}
                {note ? `: ${note}` : ''}
              </div>
            ) : null}
            {rowError ? (
              <div role="alert" className="text-danger small mt-1">
                {rowError}
              </div>
            ) : null}
          </td>
          <td className={CELL_CLASSES}>
            <IssuePriorityPill issue={issue} />
          </td>
          <td className={`${CELL_CLASSES} font-semibold text-slate-900`}>
            {tagName ? <span>{tagName}</span> : <MissingValue>{ISSUE_TYPE_NOT_AVAILABLE_COPY}</MissingValue>}
          </td>
          {showPageColumn ? (
            // A page's name wraps between its words within the column rather than being cut, so it is
            // read whole; the column keeps the width of a few words, so a narrow screen scrolls the table
            // sideways, and only a word wider than the column is broken.
            <td className={`${CELL_CLASSES} ${PAGE_CELL_WIDTH_CLASSES} wrap-break-word`}>
              {pageKey && (pageTitle || pageAddress) ? (
                <>
                  <PageName
                    inSiteFrame={inSiteFrame}
                    pageKey={pageKey}
                    pageUrl={pageAddress}
                    title={pageTitle}
                    fullTitle={resolvedPage?.name?.trim()}
                    className={PAGE_LINK_CLASSES}
                  />
                  {pageAddress ? (
                    <div className="mt-0.5 truncate font-eyebrow text-[0.6875rem] text-slate-400" title={pageAddress}>
                      {urlDisplayPath(pageAddress)}
                    </div>
                  ) : null}
                  {resolvedPage?.contentPriority ? (
                    <div className="mt-0.5">
                      <PagePriority priority={resolvedPage.contentPriority} />
                    </div>
                  ) : null}
                </>
              ) : (
                <MissingValue>{pageKey ? PAGE_NOT_AVAILABLE_COPY : NO_PAGE_COPY}</MissingValue>
              )}
            </td>
          ) : null}
          <td className={`${CELL_CLASSES} text-slate-600`}>{responsibilityLabel(issue.resolutionResponsibility)}</td>
          <td className={`${CELL_CLASSES} whitespace-nowrap text-slate-500`}>
            {openedOn ? (
              <>
                <div>
                  <span title={issue.firstDetectedAt?.format(ROW_DATE_FULL_FORMAT)}>{openedOn}</span>
                  {openDays !== undefined && openDays >= 1 ? (
                    <span className="text-slate-400"> ({openDays === 1 ? '1 day' : `${openDays} days`})</span>
                  ) : null}
                </div>
                {lastSeenOn ? (
                  <div className="mt-0.5 text-xs text-slate-400" title={issue.lastDetectedAt?.format(ROW_DATE_FULL_FORMAT)}>
                    Last seen {lastSeenOn}
                  </div>
                ) : null}
              </>
            ) : (
              <MissingValue>No first detection</MissingValue>
            )}
          </td>
          {showActionsColumn ? (
            <td className={`${CELL_CLASSES} whitespace-nowrap text-right`}>
              {displayedStatus !== IssueStatus.Open ? null : inSiteFrame ? (
                siteOptimizeHref ? (
                  // The site opens the page's own Optimize screen, in its own window rather than in the frame.
                  <a
                    href={siteOptimizeHref}
                    target="_top"
                    className={`${BP_TOOLBAR_BUTTON_SMALL_CLASSES} no-underline hover:no-underline`}
                    aria-label={`Optimize: ${tagLabel}`}
                  >
                    Optimize
                  </a>
                ) : null
              ) : (
                <div className="inline-flex items-start justify-end gap-2">
                  {api && resolvedPage && pageKey && optimizationTypesOfIssue.length ? (
                    <div className="inline-flex flex-col items-end">
                      <IssueOptimizeAction
                        api={api}
                        page={resolvedPage}
                        pageKey={pageKey}
                        pageTitle={pageTitle || pageAddress || ''}
                        issueLabel={tagLabel}
                        types={optimizationTypesOfIssue}
                        followed={pageOptimizations[pageKey]?.issueId === issue.id ? pageOptimizationViews[pageKey] : undefined}
                        pageWaiting={
                          (startingIssueIdsByPage[pageKey] ?? []).some((id) => id !== issue.id) ||
                          (!!pageOptimizations[pageKey] &&
                            pageOptimizations[pageKey].issueId !== issue.id &&
                            (pageOptimizationViews[pageKey]?.writing ?? true))
                        }
                        onStartingChange={(starting) => setIssueStarting({ pageKey, issueId: issue.id, starting })}
                        onStarted={(started) => followPageOptimization({ pageKey, issueId: issue.id, started })}
                      />
                    </div>
                  ) : null}
                  {contentTeamFixes && pageKey && (pageTitle || pageAddress) && healthCheckKey ? (
                    <Link
                      to={brandPresenceContentItemHealthPath({ key: pageKey, healthCheckKey })}
                      className={`${BP_TOOLBAR_BUTTON_SMALL_CLASSES} no-underline hover:no-underline`}
                      aria-label={`Fix: ${tagLabel}`}
                    >
                      Fix
                    </Link>
                  ) : !contentTeamFixes ? (
                    <span className="text-xs text-slate-500">{responsibilityLabel(issue.resolutionResponsibility)} fixes this</span>
                  ) : null}
                </div>
              )}
            </td>
          ) : null}
        </tr>
        <tr id={reasonPanelId} className={expanded ? 'bg-slate-50/60' : 'hidden'} aria-hidden={expanded ? undefined : true}>
          <td colSpan={columnCount} className="px-6 py-4">
            {expanded ? (
              <div className="max-w-[76ch] text-sm text-slate-600">
                <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                  Why this was flagged
                </div>
                {(issue.evidence ?? []).map((item, index) => {
                  const valueText = evidenceValueText(item.value, item.isTruncated);
                  return (
                    <div key={index} className="mt-2">
                      {item.reasoning ? <p className="mb-1">{item.reasoning}</p> : null}
                      {valueText ? (
                        <p className="mb-1 text-slate-500">
                          Found on the page: <span className="font-medium text-slate-700">{valueText}</span>
                        </p>
                      ) : null}
                    </div>
                  );
                })}
                {healthCheckKey ? (
                  <p className="mb-0 mt-2 text-slate-500">
                    Lowers the health check:{' '}
                    <span className="font-medium text-slate-700">
                      <HealthCheckNameReference api={api} healthCheckKey={healthCheckKey} fallback={NAME_NOT_AVAILABLE_COPY} />
                    </span>
                  </p>
                ) : null}
              </div>
            ) : null}
          </td>
        </tr>
      </React.Fragment>
    );
  };

  /** The one row a page gets in the grouped view, above the issues it carries. */
  const renderPageGroupRow = (pageKey: string, groupIssues: ClientIssueEntity[]) => {
    const resolvedPage = pageKey ? pagesByKey[pageKey] : undefined;
    const pageAddress = resolvedPage?.url;
    const pageTitle = pageTitleOf(resolvedPage, groupIssues[0]);
    const count = groupIssues.length;

    return (
      <tr key={`page-${pageKey || 'none'}`} className="bg-slate-50/80">
        <td colSpan={columnCount} className="px-4 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            {pageKey && (pageTitle || pageAddress) ? (
              <>
                <PageName
                  inSiteFrame={inSiteFrame}
                  pageKey={pageKey}
                  pageUrl={pageAddress}
                  title={pageTitle}
                  fullTitle={resolvedPage?.name?.trim()}
                  className={`text-sm ${PAGE_LINK_CLASSES}`}
                />
                {pageAddress ? (
                  <span className="truncate font-eyebrow text-[0.6875rem] text-slate-400" title={pageAddress}>
                    {urlDisplayPath(pageAddress)}
                  </span>
                ) : null}
                <PagePriority priority={resolvedPage?.contentPriority} />
              </>
            ) : (
              <span className="text-sm font-semibold text-slate-600">{pageKey ? PAGE_NOT_AVAILABLE_COPY : NO_PAGE_COPY}</span>
            )}
            <span className="ml-auto text-xs text-slate-500 tabular-nums">{count === 1 ? '1 issue' : `${count} issues`}</span>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
      <main className="relative mx-auto w-full max-w-[1800px] px-4 py-6 sm:px-6 sm:py-8">
        <BrandPresencePageHeader
          eyebrow={
            <>
              Brand Presence
              {/* The count is also the screen's live region: reads and changes are announced from here. */}
              <span aria-live="polite" data-testid="issues-count" className="tabular-nums">
                {countLabel ? (
                  <>
                    <span aria-hidden="true" className="px-1.5 text-slate-300">
                      /
                    </span>
                    {countLabel}
                  </>
                ) : null}
                {announcement ? <span className="sr-only">{announcement}</span> : null}
              </span>
            </>
          }
          title="Issues"
          description="What holds your pages back from a better health score, who fixes it, and where each one stands."
        />

        {/* No overflow clipping on the card: the toolbar's menus open below it and must stay whole. */}
        <section className={`${BP_CARD_CLASSES} motion-safe:animate-bp-fade-up [--bp-delay:120ms]`} aria-label="Issue list">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/70 px-4 py-3">
            <IssuesAddFilterDropdown
              api={api}
              values={{
                statusToken: statusFilter ? BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS[statusFilter] : BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN,
                issueTypeKey: issueTypeFilter,
                responsibilityToken: responsibilityFromToken(responsibilityToken) ?? '',
                pageKey: pageFilter,
                siteId: siteFilter,
              }}
              onChange={changeFilters}
              issueTypeOptions={issueTypeOptions}
              siteOptions={siteOptions}
              offerSiteFilter={hasMultipleSites}
              pageLabel={pageLabelForChip}
              namesLoading={namesLoading}
            />
            <button
              type="button"
              className={groupByPage ? BP_TOOLBAR_BUTTON_OPEN_CLASSES : BP_TOOLBAR_BUTTON_CLASSES}
              aria-pressed={groupByPage}
              onClick={() =>
                updateParams({
                  [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.view]: groupByPage ? undefined : BRAND_PRESENCE_ISSUES_VIEW_BY_PAGE_TOKEN,
                })
              }
            >
              <FontAwesomeIcon icon={faLayerGroup} className="text-xs" />
              Group by page
            </button>
          </div>

          {groupByPage ? (
            <p className="m-0 border-b border-slate-200/70 bg-slate-50/60 px-4 py-2 text-xs text-slate-500">
              Issues are grouped within the current page of the list.
            </p>
          ) : null}

          {canChangeStatus && selectedIssues.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primary/15 bg-primary/[0.05] px-4 py-2.5 motion-safe:animate-bp-slide-down">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-white tabular-nums">
                  {selectedIssues.length}
                </span>
                {selectedIssues.length === 1 ? 'issue selected' : 'issues selected'}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {ISSUE_DECISIONS.map((decision) => (
                  <button
                    key={decision.id}
                    type="button"
                    className={BP_TOOLBAR_BUTTON_SMALL_CLASSES}
                    title={decision.explanation}
                    onClick={(event) => {
                      setBulkDraft({ decision, issues: selectedIssues });
                      setBulkNote('');
                      setBulkError(undefined);
                      bulkTriggerRef.current = event.currentTarget;
                      setBulkOpen(true);
                    }}
                  >
                    {decision.action}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {bulkError ? (
            <div role="alert" className="border-b border-rose-200/70 bg-rose-50 px-4 py-2 text-sm text-rose-700">
              {bulkError}
            </div>
          ) : null}

          {failed ? (
            <Alert variant="danger" className="mx-4 mt-3">
              Your issues could not be loaded.{' '}
              <Button variant="danger" size="sm" onClick={retry}>
                Try again
              </Button>
            </Alert>
          ) : null}

          <IndeterminateProgress active={isReloading} label="Loading issues" />

          {/* The table takes the height of its rows and the page scrolls it; a table wider than the
          screen scrolls sideways in this region alone. */}
          <div
            ref={tableRegionRef}
            className="overflow-x-auto focus-visible:outline-none"
            tabIndex={0}
            role="region"
            aria-label="Issues"
            aria-busy={isReloading}
          >
            <Table hover className="mb-0 w-full text-left">
              <thead>
                <tr className="bg-slate-50/70">
                  <th scope="col" className={`${BP_TABLE_HEAD_CELL_CLASSES} w-16`}>
                    {canChangeStatus && selectableIssues.length > 0 ? (
                      <Form.Check
                        type="checkbox"
                        className="mb-0"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select every issue on this page"
                      />
                    ) : (
                      <span className="sr-only">Details</span>
                    )}
                  </th>
                  <th scope="col" className={BP_TABLE_HEAD_CELL_CLASSES}>
                    Status
                  </th>
                  <th scope="col" className={BP_TABLE_HEAD_CELL_CLASSES}>
                    Priority
                  </th>
                  <th scope="col" className={BP_TABLE_HEAD_CELL_CLASSES}>
                    Issue type
                  </th>
                  {showPageColumn ? (
                    <th scope="col" className={BP_TABLE_HEAD_CELL_CLASSES}>
                      Page
                    </th>
                  ) : null}
                  <th scope="col" className={BP_TABLE_HEAD_CELL_CLASSES}>
                    Who fixes it
                  </th>
                  <th scope="col" className={BP_TABLE_HEAD_CELL_CLASSES}>
                    Open since
                  </th>
                  {showActionsColumn ? (
                    <th scope="col" className={`${BP_TABLE_HEAD_CELL_CLASSES} text-right`}>
                      Actions
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isInitialLoading || (!hasRows && (loading || steppingBack)) ? (
                  Array.from({ length: SKELETON_ROW_COUNT }, (_unused, index) => (
                    <tr key={`skeleton-${index}`} aria-hidden="true">
                      {Array.from({ length: columnCount }, (_column, columnIndex) => (
                        <td
                          key={columnIndex}
                          className={`px-4 py-4 ${showPageColumn && columnIndex === PAGE_COLUMN_INDEX ? PAGE_CELL_WIDTH_CLASSES : ''}`}
                        >
                          <SkeletonBlock className={`h-4 ${SKELETON_CELL_WIDTHS[(index + columnIndex) % SKELETON_CELL_WIDTHS.length]}`} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : hasRows ? (
                  groupByPage ? (
                    groupedByPage.map((group) => (
                      <React.Fragment key={`group-${group.pageKey || 'none'}`}>
                        {renderPageGroupRow(group.pageKey, group.issues)}
                        {group.issues.map((issue) => renderIssueRow(issue))}
                      </React.Fragment>
                    ))
                  ) : (
                    visibleIssues.map((issue) => renderIssueRow(issue))
                  )
                ) : !failed ? (
                  <tr>
                    <td colSpan={columnCount} className="p-0">
                      {hasActiveFilters ? (
                        <BrandPresenceEmptyState
                          icon={faMagnifyingGlass}
                          title="No issues match these filters."
                          description="Try another status or issue type, or clear the filters to see every issue."
                          actions={
                            <button type="button" className={BP_TOOLBAR_BUTTON_SMALL_CLASSES} onClick={clearFilters}>
                              Clear filters
                            </button>
                          }
                        />
                      ) : (
                        <BrandPresenceEmptyState
                          icon={faCircleCheck}
                          title={
                            inSiteFrame ? 'No issues have been found on this site yet.' : 'No issues have been found in this project yet.'
                          }
                          description="Issues appear here when a page's health checks find something to fix."
                          actions={
                            inSiteFrame ? undefined : (
                              <Link to={BRAND_PRESENCE_CONTENT_INVENTORY_PATH} className={BP_TOOLBAR_BUTTON_CLASSES}>
                                Open Content Inventory
                              </Link>
                            )
                          }
                        />
                      )}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>

          {!failed && pageMeta && displayedCount > 0 ? (
            // The pager the other Brand Presence lists end in (the content inventory, the jobs): it pages
            // through the pages Sync Core says the list has, counts what the header counts, offers the rows a
            // page shows, and wraps onto more lines where the card is too narrow for one, so nothing of it
            // runs past the card on a phone. While the list is read - after a change, or on the way back from
            // a page that answered empty - it stays, says it is busy and takes no page change, since the
            // pages it would move between are about to change.
            <div data-testid="issues-pager" aria-busy={loading}>
              <BrandPresencePagination
                page={Math.max(0, Math.min(pageMeta.page, pageMeta.numberOfPages - 1))}
                numberOfPages={Math.max(1, pageMeta.numberOfPages)}
                itemsPerPage={pageSize}
                totalNumberOfItems={displayedCount}
                itemLabel={displayedCount === 1 ? 'issue' : 'issues'}
                onPageChange={setPage}
                shownOnPage={hasRows && removedVisibleCount > 0 ? visibleIssues.length : undefined}
                busy={loading}
                pageSizeOptions={BRAND_PRESENCE_LIST_PAGE_SIZES}
                onPageSizeChange={(nextPageSize) =>
                  updateParams({ [BRAND_PRESENCE_ISSUES_QUERY_PARAMS.pageSize]: pageSizeToToken(nextPageSize) })
                }
              />
            </div>
          ) : null}
        </section>
      </main>

      {/* The optimizations the list follows, one per page, whatever became of the rows that started them. */}
      {api
        ? Object.entries(pageOptimizations).map(([pageKey, { started }]) => (
            <IssueOptimizationFollower
              key={`${pageKey}:${started.optimizationId}`}
              api={api}
              started={started}
              onView={(view) => setPageOptimizationViews((current) => ({ ...current, [pageKey]: view }))}
            />
          ))
        : null}

      {/* The dialog traps focus while it is open and restores it once it has gone. Cancelled, that
          restore is the whole story and focus is back on the row's control; confirmed, `afterClose`
          runs after the restore and focus follows the outcome of that same verdict's change from
          there — an answer belonging to a verdict since replaced moves nothing. */}
      <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show={verdictOpen} onHide={closeVerdict} afterClose={verdictClosed}>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void confirmVerdict();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{verdictDraft?.verdict.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {verdictDraft?.tagName ? (
                <>
                  <strong>{verdictDraft.tagName}</strong>.{' '}
                </>
              ) : null}
              {verdictDraft?.verdict.explanation}
            </p>
            <Form.Group>
              <Form.Label htmlFor={VERDICT_NOTE_ID}>Reason (optional)</Form.Label>
              <Form.Control
                id={VERDICT_NOTE_ID}
                as="textarea"
                rows={3}
                maxLength={ISSUE_NOTE_MAX_LENGTH}
                value={verdictNote}
                onChange={(event) => setVerdictNote(event.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeVerdict}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {verdictDraft?.verdict.decision.action}
            </Button>
          </Modal.Footer>
        </Form>
      </EmbeddedModal>

      {/* The one confirmation of a bulk decision, with the one shared reason it saves on every row. */}
      {/* Cancelled, the dialog hands focus back to the bulk bar. Confirmed, the bar may have gone with the
          selection, and then the table takes focus, as it does after a row's own change. */}
      <EmbeddedModal className={BP_PORTAL_CLASS_NAME} show={bulkOpen} onHide={() => setBulkOpen(false)} afterClose={bulkClosed}>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void confirmBulk();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{bulkDraft ? bulkTitle(bulkDraft.decision, bulkDraft.issues.length) : ''}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {bulkDraft?.decision.explanation}
              {bulkDraft && verdictOf(bulkDraft.decision) ? ` ${verdictOf(bulkDraft.decision)?.explanation}` : ''}
            </p>
            <Form.Group>
              <Form.Label htmlFor={BULK_NOTE_ID}>Reason (optional)</Form.Label>
              <Form.Control
                id={BULK_NOTE_ID}
                as="textarea"
                rows={3}
                maxLength={ISSUE_NOTE_MAX_LENGTH}
                value={bulkNote}
                onChange={(event) => setBulkNote(event.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" aria-disabled={bulkPending}>
              {bulkDraft?.decision.action}
            </Button>
          </Modal.Footer>
        </Form>
      </EmbeddedModal>
    </div>
  );
}
